import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Printer, Check, Clock, ChevronDown, ChevronRight, Pen, X, Download, FolderOpen, UserCheck, ShieldCheck, Edit } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PayslipArchive = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [archive, setArchive] = useState([]);
  const [totalPayslips, setTotalPayslips] = useState(0);
  const [expandedPeriods, setExpandedPeriods] = useState({});
  
  // Payslip view dialog
  const [showPayslipDialog, setShowPayslipDialog] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [actualCABalance, setActualCABalance] = useState(null);
  const [loadingCABalance, setLoadingCABalance] = useState(false);
  
  // Edit payslip dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPayslip, setEditingPayslip] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Signature dialog
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signingPayslip, setSigningPayslip] = useState(null);
  const [signatureType, setSignatureType] = useState('employee'); // 'employee' or 'admin'
  const [signing, setSigning] = useState(false);
  const [confirmationName, setConfirmationName] = useState(''); // Name typed to confirm

  // Permission checks
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin';
  const isAlma = username.includes('alma') || fullName.includes('alma');
  const isArbe = username.includes('arbe') || fullName.includes('arbe');
  const isPabs = username.includes('pabs') || fullName.includes('pabs');
  const canReleasePayslip = isAdmin || isAlma || isArbe || isPabs;
  const canEditPayslip = isAdmin || isAlma || isArbe || isPabs;

  useEffect(() => {
    fetchArchive();
  }, []);

  const fetchArchive = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/payslips/archive`);
      setArchive(response.data.archive || []);
      setTotalPayslips(response.data.total_payslips || 0);
      
      // Auto-expand first period
      if (response.data.archive?.length > 0) {
        setExpandedPeriods({ [response.data.archive[0].period_label]: true });
      }
    } catch (error) {
      console.error('Error fetching archive:', error);
      toast.error('Failed to load payslip archive');
    } finally {
      setLoading(false);
    }
  };

  const togglePeriod = (periodLabel) => {
    setExpandedPeriods(prev => ({
      ...prev,
      [periodLabel]: !prev[periodLabel]
    }));
  };

  const openPayslipDialog = async (payslip) => {
    setSelectedPayslip(payslip);
    setShowPayslipDialog(true);
    setActualCABalance(null);
    
    // Fetch actual CA balance for this user
    if (payslip.user_id) {
      setLoadingCABalance(true);
      try {
        const res = await axios.get(`${API}/cash-advance/user/${payslip.user_id}/total-balance`);
        setActualCABalance(res.data);
      } catch (error) {
        console.error('Error fetching CA balance:', error);
        setActualCABalance(null);
      } finally {
        setLoadingCABalance(false);
      }
    }
  };

  // Edit payslip functions
  const openEditDialog = (payslip) => {
    setEditingPayslip(payslip);
    setEditForm({
      half_month_salary: payslip.half_month_salary || 0,
      overtime_pay: payslip.overtime_pay || 0,
      holiday_pay: payslip.holiday_pay || 0,
      bonus: payslip.bonus || 0,
      sss: payslip.sss || 0,
      philhealth: payslip.philhealth || 0,
      pagibig: payslip.pagibig || 0,
      tax: payslip.tax || 0,
      cash_advance: payslip.cash_advance || 0,
      other_deductions: payslip.other_deductions || 0
    });
    setShowEditDialog(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const savePayslipEdit = async () => {
    if (!editingPayslip) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/payslips/${editingPayslip.id}`, {
        timesheet_id: editingPayslip.timesheet_id,
        user_id: editingPayslip.user_id,
        user_name: editingPayslip.user_name,
        period_start: editingPayslip.period_start,
        period_end: editingPayslip.period_end,
        days_worked: editingPayslip.days_worked,
        absences: editingPayslip.absences,
        ...editForm
      });
      
      toast.success('Payslip updated successfully');
      setShowEditDialog(false);
      setEditingPayslip(null);
      fetchArchive(); // Refresh the list
    } catch (error) {
      console.error('Error updating payslip:', error);
      toast.error(error.response?.data?.detail || 'Failed to update payslip');
    } finally {
      setSaving(false);
    }
  };

  const openSignatureDialog = (payslip, type = 'employee') => {
    setSigningPayslip(payslip);
    setSignatureType(type);
    setShowSignatureDialog(true);
    setConfirmationName(''); // Clear name input when opening
  };

  // Clear confirmation name
  const clearSignature = () => {
    setConfirmationName('');
  };

  const submitSignature = async () => {
    if (!signingPayslip) return;
    
    // Check if name is entered
    if (!confirmationName.trim()) {
      toast.error('Please type your name to confirm');
      return;
    }
    
    setSigning(true);
    try {
      // Create a simple text-based signature (name + timestamp)
      const signatureData = `CONFIRMED: ${confirmationName.trim()} - ${new Date().toISOString()}`;
      await axios.post(`${API}/payslips/${signingPayslip.id}/sign`, {
        signature_data: signatureData,
        signature_type: signatureType
      });
      
      if (signatureType === 'employee') {
        toast.success('Payslip signed! Waiting for admin to confirm salary release.');
      } else {
        toast.success('Salary release confirmed! Payslip archived.');
      }
      
      setShowSignatureDialog(false);
      setSigningPayslip(null);
      setConfirmationName('');
      fetchArchive();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign payslip');
    } finally {
      setSigning(false);
    }
  };

  const handlePrint = (payslip) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payslip - ${payslip.user_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header p { margin: 5px 0; color: #666; }
            .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
            .section { margin: 20px 0; }
            .section-title { font-weight: bold; background: #f0f0f0; padding: 8px; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; }
            td { padding: 8px; border-bottom: 1px solid #eee; }
            td:last-child { text-align: right; }
            .total-row { font-weight: bold; font-size: 18px; background: #f5f5f5; }
            .ca-info { background: #fff8e1; padding: 10px; margin: 15px 0; border: 1px solid #ffc107; border-radius: 4px; }
            .signature-section { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 20px; }
            .signatures-grid { display: flex; gap: 40px; }
            .signature-box { flex: 1; }
            .signature-box h4 { margin: 0 0 10px 0; font-size: 14px; }
            .signature-box img { max-width: 200px; max-height: 80px; border: 1px solid #eee; }
            .signature-line { border-top: 1px solid #000; width: 200px; margin-top: 60px; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>PAYSLIP</h1>
            <p>WAGA Internet Services</p>
          </div>
          
          <div class="info-row">
            <div><strong>Employee:</strong> ${payslip.user_name}</div>
            <div><strong>Pay Period:</strong> ${payslip.period_start} to ${payslip.period_end}</div>
          </div>
          
          <div class="section">
            <div class="section-title">Earnings</div>
            <table>
              <tr><td>Basic Salary (Half Month)</td><td>₱${payslip.half_month_salary?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr><td>Overtime Pay</td><td>₱${payslip.overtime_pay?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr><td>Holiday Pay</td><td>₱${payslip.holiday_pay?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr style="font-weight: bold; background: #e8f5e9;"><td>Gross Pay</td><td>₱${payslip.gross_pay?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">Deductions</div>
            <table>
              <tr><td>SSS</td><td>₱${payslip.sss?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr><td>PhilHealth</td><td>₱${payslip.philhealth?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr><td>Pag-IBIG</td><td>₱${payslip.pagibig?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr><td>Tax</td><td>₱${payslip.tax?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr><td>Cash Advance</td><td>₱${payslip.cash_advance?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
              <tr style="font-weight: bold; background: #ffebee;"><td>Total Deductions</td><td>₱${payslip.total_deductions?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
            </table>
          </div>
          
          ${payslip.cash_advance_remaining > 0 ? `
          <div class="ca-info">
            <strong>Remaining Cash Advance Balance:</strong> ₱${payslip.cash_advance_remaining?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}
            <br><small style="color: #666;">* Cash advance is deducted manually as per admin discretion</small>
          </div>
          ` : ''}
          
          <table>
            <tr class="total-row"><td>NET PAY</td><td>₱${payslip.net_pay?.toLocaleString('en-PH', {minimumFractionDigits: 2}) || '0.00'}</td></tr>
          </table>
          
          <div class="signature-section">
            <div class="signatures-grid">
              <div class="signature-box">
                <h4>Employee Signature (Acknowledgment):</h4>
                ${payslip.employee_signature_data || payslip.signature_data 
                  ? `<img src="${payslip.employee_signature_data || payslip.signature_data}" alt="Employee Signature" /><br>
                     <small style="color: #666;">Signed: ${payslip.employee_signed_at || payslip.signed_at ? new Date(payslip.employee_signed_at || payslip.signed_at).toLocaleString() : 'N/A'}</small>` 
                  : '<div class="signature-line"></div>'}
              </div>
              <div class="signature-box">
                <h4>Admin Signature (Salary Released):</h4>
                ${payslip.admin_signature_data 
                  ? `<img src="${payslip.admin_signature_data}" alt="Admin Signature" /><br>
                     <small style="color: #666;">Released by: ${payslip.admin_signed_by || 'Admin'}<br>Date: ${payslip.admin_signed_at ? new Date(payslip.admin_signed_at).toLocaleString() : 'N/A'}</small>` 
                  : '<div class="signature-line"></div>'}
              </div>
            </div>
          </div>
          
          <div class="footer">
            <p>This is a computer-generated payslip.</p>
            <p>Generated on: ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  // Check if employee can sign (hasn't signed yet and owns the payslip)
  const canEmployeeSign = (payslip) => {
    const hasEmployeeSignature = payslip.employee_signature_data || payslip.signature_data;
    return payslip.user_id === user?.id && !hasEmployeeSignature;
  };

  // Check if admin can sign (employee has signed, admin hasn't)
  const canAdminSign = (payslip) => {
    const hasEmployeeSignature = payslip.employee_signature_data || payslip.signature_data;
    const hasAdminSignature = payslip.admin_signature_data;
    return canReleasePayslip && hasEmployeeSignature && !hasAdminSignature;
  };

  // Get payslip status badge
  const getPayslipStatus = (payslip) => {
    const hasEmployeeSignature = payslip.employee_signature_data || payslip.signature_data;
    const hasAdminSignature = payslip.admin_signature_data;
    const status = payslip.status;
    
    if (hasAdminSignature || status === 'released') {
      return { label: 'Released', color: 'bg-emerald-100 text-emerald-700', icon: ShieldCheck };
    } else if (hasEmployeeSignature || status === 'employee_signed') {
      return { label: 'Awaiting Release', color: 'bg-amber-100 text-amber-700', icon: Clock };
    } else {
      return { label: 'Pending Employee', color: 'bg-gray-100 text-gray-700', icon: Clock };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-2 sm:p-4 md:p-6" data-testid="payslip-archive-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Payslip Archive</h1>
          <p className="text-sm text-slate-600">View and sign payslips organized by cut-off period</p>
        </div>
        <Badge className="bg-blue-100 text-blue-700 self-start">
          {totalPayslips} Total Payslips
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-xs">Total Periods</p>
            <p className="text-2xl font-bold">{archive.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <p className="text-emerald-100 text-xs">Released</p>
            <p className="text-2xl font-bold">{archive.reduce((acc, p) => acc + (p.released_count || 0), 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <p className="text-amber-100 text-xs">Awaiting Release</p>
            <p className="text-2xl font-bold">{archive.reduce((acc, p) => acc + (p.pending_admin_count || p.pending_count || 0), 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <p className="text-purple-100 text-xs">Total Payslips</p>
            <p className="text-2xl font-bold">{totalPayslips}</p>
          </CardContent>
        </Card>
      </div>

      {/* Archive List */}
      {archive.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FolderOpen size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600">No payslips found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {archive.map((period) => (
            <Card key={period.period_label} className="overflow-hidden">
              {/* Period Header - Clickable */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => togglePeriod(period.period_label)}
                data-testid={`period-${period.period_start}`}
              >
                <div className="flex items-center gap-3">
                  {expandedPeriods[period.period_label] ? (
                    <ChevronDown size={20} className="text-slate-400" />
                  ) : (
                    <ChevronRight size={20} className="text-slate-400" />
                  )}
                  <FolderOpen size={20} className="text-blue-500" />
                  <div>
                    <h3 className="font-semibold text-slate-900">{period.period_label}</h3>
                    <p className="text-xs text-slate-500">
                      {period.payslips.length} payslip(s) • Total: {formatCurrency(period.total_net_pay)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {(period.released_count || 0) > 0 && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                      <ShieldCheck size={12} className="mr-1" />
                      {period.released_count || 0} Released
                    </Badge>
                  )}
                  {(period.pending_admin_count || 0) > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">
                      <Clock size={12} className="mr-1" />
                      {period.pending_admin_count || 0} Awaiting Release
                    </Badge>
                  )}
                  {(period.pending_employee_count || period.pending_count || 0) > 0 && (
                    <Badge className="bg-gray-100 text-gray-700 text-xs">
                      <Clock size={12} className="mr-1" />
                      {period.pending_employee_count || period.pending_count || 0} Pending
                    </Badge>
                  )}
                </div>
              </div>

              {/* Payslips List */}
              {expandedPeriods[period.period_label] && (
                <div className="border-t border-slate-100">
                  <div className="divide-y divide-slate-100">
                    {period.payslips.map((payslip) => (
                      <div
                        key={payslip.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 hover:bg-slate-50 gap-3"
                        data-testid={`payslip-${payslip.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText size={18} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{payslip.user_name}</p>
                            <p className="text-sm text-slate-500">
                              Net Pay: <span className="font-semibold text-emerald-600">{formatCurrency(payslip.net_pay)}</span>
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-auto flex-wrap">
                          {/* Status Badge */}
                          {(() => {
                            const status = getPayslipStatus(payslip);
                            const StatusIcon = status.icon;
                            return (
                              <Badge className={status.color}>
                                <StatusIcon size={14} className="mr-1" />
                                {status.label}
                              </Badge>
                            );
                          })()}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPayslipDialog(payslip)}
                            data-testid={`view-payslip-${payslip.id}`}
                          >
                            View
                          </Button>
                          
                          {/* Edit Button - Only for Arbe, Alma, Pabs */}
                          {canEditPayslip && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(payslip)}
                              data-testid={`edit-payslip-${payslip.id}`}
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            >
                              <Edit size={14} className="mr-1" />
                              Edit
                            </Button>
                          )}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePrint(payslip)}
                            data-testid={`print-payslip-${payslip.id}`}
                          >
                            <Printer size={14} />
                          </Button>
                          
                          {/* Employee Sign Button */}
                          {canEmployeeSign(payslip) && (
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => openSignatureDialog(payslip, 'employee')}
                              data-testid={`employee-sign-payslip-${payslip.id}`}
                            >
                              <Pen size={14} className="mr-1" />
                              Sign
                            </Button>
                          )}
                          
                          {/* Admin Release Button */}
                          {canAdminSign(payslip) && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => openSignatureDialog(payslip, 'admin')}
                              data-testid={`admin-sign-payslip-${payslip.id}`}
                            >
                              <ShieldCheck size={14} className="mr-1" />
                              Confirm Release
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Payslip View Dialog */}
      <Dialog open={showPayslipDialog} onOpenChange={setShowPayslipDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payslip Details</DialogTitle>
            <DialogDescription>
              {selectedPayslip?.user_name} • {selectedPayslip?.period_start} to {selectedPayslip?.period_end}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPayslip && (
            <div className="space-y-4">
              {/* Earnings */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Earnings</h4>
                <div className="bg-green-50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Half Month Salary</span>
                    <span className="font-medium">{formatCurrency(selectedPayslip.half_month_salary)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Overtime Pay</span>
                    <span className="text-green-600">{formatCurrency(selectedPayslip.overtime_pay)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Holiday Pay</span>
                    <span className="text-green-600">{formatCurrency(selectedPayslip.holiday_pay)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bonus</span>
                    <span className="text-green-600">{formatCurrency(selectedPayslip.bonus)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-green-700 border-t pt-2">
                    <span>Gross Pay</span>
                    <span>{formatCurrency(selectedPayslip.gross_pay)}</span>
                  </div>
                </div>
              </div>

              {/* Deductions - Always show all */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Deductions</h4>
                <div className="bg-red-50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between"><span>SSS</span><span className="text-red-600">{formatCurrency(selectedPayslip.sss)}</span></div>
                  <div className="flex justify-between"><span>PhilHealth</span><span className="text-red-600">{formatCurrency(selectedPayslip.philhealth)}</span></div>
                  <div className="flex justify-between"><span>Pag-IBIG</span><span className="text-red-600">{formatCurrency(selectedPayslip.pagibig)}</span></div>
                  <div className="flex justify-between"><span>Tax</span><span className="text-red-600">{formatCurrency(selectedPayslip.tax)}</span></div>
                  <div className="flex justify-between"><span>Other Deductions</span><span className="text-red-600">{formatCurrency(selectedPayslip.other_deductions)}</span></div>
                  <div className="flex justify-between font-semibold text-red-700 border-t pt-2">
                    <span>Total Deductions</span>
                    <span>{formatCurrency(selectedPayslip.total_deductions)}</span>
                  </div>
                </div>
              </div>

              {/* Cash Advance Section - Always show */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Cash Advance</h4>
                <div className="bg-amber-50 rounded-lg p-3 space-y-2 text-sm">
                  {loadingCABalance ? (
                    <p className="text-amber-600 text-center">Loading CA balance...</p>
                  ) : actualCABalance ? (
                    <>
                      <div className="flex justify-between">
                        <span>Total Cash Advance Balance</span>
                        <span className="text-amber-700">{formatCurrency((actualCABalance.total_ca_balance || 0) + (selectedPayslip.cash_advance || 0))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cash Advance Deduction</span>
                        <span className="text-red-600">-{formatCurrency(selectedPayslip.cash_advance)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-amber-700 border-t pt-2">
                        <span>Cash Advance Remaining</span>
                        <span>{formatCurrency(actualCABalance.total_ca_balance)}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span>Cash Advance Deduction</span>
                        <span className="text-red-600">-{formatCurrency(selectedPayslip.cash_advance)}</span>
                      </div>
                      {selectedPayslip.cash_advance_remaining > 0 && (
                        <div className="flex justify-between font-semibold text-amber-700 border-t pt-2">
                          <span>Cash Advance Remaining (at time of payslip)</span>
                          <span>{formatCurrency(selectedPayslip.cash_advance_remaining)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Net Pay */}
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-blue-600">Net Pay</p>
                <p className="text-3xl font-bold text-blue-700">{formatCurrency(selectedPayslip.net_pay)}</p>
              </div>

              {/* Signatures Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-slate-700">Signatures</h4>
                
                {/* Employee Signature */}
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600 mb-2">Employee (Acknowledgment):</p>
                    {(selectedPayslip.employee_signature_data || selectedPayslip.signature_data) ? (
                      <div>
                        <img 
                          src={selectedPayslip.employee_signature_data || selectedPayslip.signature_data} 
                          alt="Employee Signature" 
                          className="max-w-[150px] border rounded"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Signed: {selectedPayslip.employee_signed_at || selectedPayslip.signed_at 
                            ? new Date(selectedPayslip.employee_signed_at || selectedPayslip.signed_at).toLocaleString() 
                            : 'N/A'}
                        </p>
                      </div>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600">Pending</Badge>
                    )}
                  </div>
                  
                  {/* Admin Signature */}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600 mb-2">Admin (Salary Released):</p>
                    {selectedPayslip.admin_signature_data ? (
                      <div>
                        <img 
                          src={selectedPayslip.admin_signature_data} 
                          alt="Admin Signature" 
                          className="max-w-[150px] border rounded"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Released by: {selectedPayslip.admin_signed_by || 'Admin'}
                          <br />
                          {selectedPayslip.admin_signed_at 
                            ? new Date(selectedPayslip.admin_signed_at).toLocaleString() 
                            : 'N/A'}
                        </p>
                      </div>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-600">
                        {(selectedPayslip.employee_signature_data || selectedPayslip.signature_data) 
                          ? 'Awaiting Release' 
                          : 'Pending Employee'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end flex-wrap">
                <Button variant="outline" onClick={() => handlePrint(selectedPayslip)}>
                  <Printer size={16} className="mr-2" />
                  Print
                </Button>
                {canEmployeeSign(selectedPayslip) && (
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      setShowPayslipDialog(false);
                      openSignatureDialog(selectedPayslip, 'employee');
                    }}
                  >
                    <Pen size={16} className="mr-2" />
                    Sign Payslip
                  </Button>
                )}
                {canAdminSign(selectedPayslip) && (
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      setShowPayslipDialog(false);
                      openSignatureDialog(selectedPayslip, 'admin');
                    }}
                  >
                    <ShieldCheck size={16} className="mr-2" />
                    Confirm Release
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {signatureType === 'employee' ? 'Sign Payslip' : 'Confirm Salary Release'}
            </DialogTitle>
            <DialogDescription>
              {signatureType === 'employee' 
                ? 'Type your full name below to acknowledge receipt of this payslip'
                : 'Type your full name below to confirm the salary has been released'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center text-sm text-slate-600">
              <p><strong>{signingPayslip?.user_name}</strong></p>
              <p>Period: {signingPayslip?.period_start} to {signingPayslip?.period_end}</p>
              <p className="text-lg font-bold text-emerald-600 mt-2">
                Net Pay: {formatCurrency(signingPayslip?.net_pay)}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Type your full name to confirm:
              </label>
              <Input
                type="text"
                placeholder="Enter your full name"
                value={confirmationName}
                onChange={(e) => setConfirmationName(e.target.value)}
                className="text-lg py-6"
                autoFocus
              />
              <p className="text-xs text-slate-500">
                By typing your name, you confirm that you have received and reviewed this payslip.
              </p>
            </div>

            {confirmationName.trim() && (
              <Button 
                variant="outline" 
                onClick={clearSignature}
                className="w-full"
              >
                <X size={16} className="mr-2" />
                Clear
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignatureDialog(false)}>
              Cancel
            </Button>
            <Button 
              className={signatureType === 'admin' ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}
              onClick={submitSignature}
              disabled={signing || !confirmationName.trim()}
            >
              {signing ? 'Confirming...' : (signatureType === 'admin' ? 'Confirm Release' : 'Confirm & Sign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payslip Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Payslip</DialogTitle>
            <DialogDescription>
              {editingPayslip?.user_name} • {editingPayslip?.period_start} to {editingPayslip?.period_end}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Earnings Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800 border-b pb-2">Earnings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Half Month Salary</Label>
                  <Input
                    type="number"
                    value={editForm.half_month_salary || 0}
                    onChange={(e) => handleEditFormChange('half_month_salary', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Overtime Pay</Label>
                  <Input
                    type="number"
                    value={editForm.overtime_pay || 0}
                    onChange={(e) => handleEditFormChange('overtime_pay', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Holiday Pay</Label>
                  <Input
                    type="number"
                    value={editForm.holiday_pay || 0}
                    onChange={(e) => handleEditFormChange('holiday_pay', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Bonus</Label>
                  <Input
                    type="number"
                    value={editForm.bonus || 0}
                    onChange={(e) => handleEditFormChange('bonus', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Deductions Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800 border-b pb-2">Deductions</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SSS</Label>
                  <Input
                    type="number"
                    value={editForm.sss || 0}
                    onChange={(e) => handleEditFormChange('sss', e.target.value)}
                  />
                </div>
                <div>
                  <Label>PhilHealth</Label>
                  <Input
                    type="number"
                    value={editForm.philhealth || 0}
                    onChange={(e) => handleEditFormChange('philhealth', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Pag-IBIG</Label>
                  <Input
                    type="number"
                    value={editForm.pagibig || 0}
                    onChange={(e) => handleEditFormChange('pagibig', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Tax</Label>
                  <Input
                    type="number"
                    value={editForm.tax || 0}
                    onChange={(e) => handleEditFormChange('tax', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Cash Advance Deduction</Label>
                  <Input
                    type="number"
                    value={editForm.cash_advance || 0}
                    onChange={(e) => handleEditFormChange('cash_advance', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Other Deductions</Label>
                  <Input
                    type="number"
                    value={editForm.other_deductions || 0}
                    onChange={(e) => handleEditFormChange('other_deductions', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Preview Calculation */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-800 mb-2">Preview</h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between font-medium text-emerald-600">
                  <span>Estimated Net Pay:</span>
                  <span>
                    {formatCurrency(
                      (editForm.half_month_salary || 0) - 
                      ((editForm.half_month_salary || 0) / 13 * (editingPayslip?.absences || 0)) +
                      (editForm.overtime_pay || 0) +
                      (editForm.holiday_pay || 0) +
                      (editForm.bonus || 0) -
                      (editForm.sss || 0) -
                      (editForm.philhealth || 0) -
                      (editForm.pagibig || 0) -
                      (editForm.tax || 0) -
                      (editForm.cash_advance || 0) -
                      (editForm.other_deductions || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600"
              onClick={savePayslipEdit}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayslipArchive;
